import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAccount, usePublicClient } from 'wagmi';
import { parseAbiItem } from 'viem';
import { scroogeAbi, SCROOGE_ADDRESS, SCROOGE_DEPLOY_BLOCK, IS_DEPLOYED } from '../../lib/contract';
import { mockStore } from '../../lib/mockStore';

// Event-сигнатуры для getLogs (topic-filter по indexed user/sponsor).
const EVT = {
  Deposited: parseAbiItem(
    'event Deposited(address indexed user, uint256 indexed depositIdx, uint8 planId, uint256 amount)',
  ),
  Claimed: parseAbiItem(
    'event Claimed(address indexed user, uint256 indexed depositIdx, uint256 gross, uint256 fee, uint256 netToUser)',
  ),
  Reinvested: parseAbiItem(
    'event Reinvested(address indexed user, uint256 indexed srcDepositIdx, uint256 newDepositIdx, uint8 newPlanId, uint256 gross, uint256 fee, uint256 reinvested)',
  ),
  Withdrawn: parseAbiItem(
    'event Withdrawn(address indexed user, uint256 indexed depositIdx, uint256 principal)',
  ),
  ReferralClaimed: parseAbiItem(
    'event ReferralClaimed(address indexed user, uint256 amount)',
  ),
};

// Адаптивный getLogs с бинарным сжатием окна при превышении лимита RPC.
async function getLogsAdaptive(client, { address, event, args, fromBlock, toBlock }) {
  try {
    return await client.getLogs({ address, event, args, fromBlock, toBlock });
  } catch (e) {
    const msg = String(e?.message ?? '');
    if (
      /more than .* results|range is too wide|limit exceeded|query timeout/i.test(msg) &&
      toBlock - fromBlock > 1n
    ) {
      const mid = fromBlock + (toBlock - fromBlock) / 2n;
      const [a, b] = await Promise.all([
        getLogsAdaptive(client, { address, event, args, fromBlock, toBlock: mid }),
        getLogsAdaptive(client, { address, event, args, fromBlock: mid + 1n, toBlock }),
      ]);
      return [...a, ...b];
    }
    throw e;
  }
}

export function useUserEventLogs() {
  const { address } = useAccount();
  const publicClient = usePublicClient();

  const mockSnap = useSyncExternalStore(
    mockStore.subscribe,
    () => mockStore.getSnapshot(address || 'anon'),
    () => mockStore.getSnapshot(address || 'anon'),
  );

  const realQuery = useQuery({
    queryKey: ['userEventLogs', address],
    enabled: Boolean(address) && Boolean(publicClient) && IS_DEPLOYED,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      if (!publicClient || !address) return [];
      const latest = await publicClient.getBlockNumber();
      const fromBlock = SCROOGE_DEPLOY_BLOCK;
      const toBlock = latest;

      const [dep, clm, rei, wdr, rfc] = await Promise.all([
        getLogsAdaptive(publicClient, {
          address: SCROOGE_ADDRESS,
          event: EVT.Deposited,
          args: { user: address },
          fromBlock,
          toBlock,
        }),
        getLogsAdaptive(publicClient, {
          address: SCROOGE_ADDRESS,
          event: EVT.Claimed,
          args: { user: address },
          fromBlock,
          toBlock,
        }),
        getLogsAdaptive(publicClient, {
          address: SCROOGE_ADDRESS,
          event: EVT.Reinvested,
          args: { user: address },
          fromBlock,
          toBlock,
        }),
        getLogsAdaptive(publicClient, {
          address: SCROOGE_ADDRESS,
          event: EVT.Withdrawn,
          args: { user: address },
          fromBlock,
          toBlock,
        }),
        getLogsAdaptive(publicClient, {
          address: SCROOGE_ADDRESS,
          event: EVT.ReferralClaimed,
          args: { user: address },
          fromBlock,
          toBlock,
        }),
      ]);

      const merged = [
        ...dep.map((l) => ({ ...l, type: 'Deposited' })),
        ...clm.map((l) => ({ ...l, type: 'Claimed' })),
        ...rei.map((l) => ({ ...l, type: 'Reinvested' })),
        ...wdr.map((l) => ({ ...l, type: 'Withdrawn' })),
        ...rfc.map((l) => ({ ...l, type: 'ReferralClaimed' })),
      ];

      merged.sort((a, b) => {
        if (a.blockNumber !== b.blockNumber) {
          return Number(b.blockNumber - a.blockNumber);
        }
        return b.logIndex - a.logIndex;
      });

      // Lazy-fetch timestamps блоков только для топовой страницы (первые 50)
      const uniqueBlocks = Array.from(
        new Set(merged.slice(0, 50).map((l) => l.blockNumber)),
      );
      const blockMap = new Map();
      await Promise.all(
        uniqueBlocks.map(async (bn) => {
          try {
            const b = await publicClient.getBlock({ blockNumber: bn });
            blockMap.set(bn, Number(b.timestamp));
          } catch {
            /* ignore */
          }
        }),
      );

      return merged.map((l) => ({
        type: l.type,
        blockNumber: l.blockNumber,
        timestamp: blockMap.get(l.blockNumber) ?? null,
        hash: l.transactionHash,
        logIndex: l.logIndex,
        args: l.args,
      }));
    },
  });

  if (!IS_DEPLOYED) {
    return {
      data: mockSnap.events,
      isLoading: false,
      error: null,
      refetch: () => {},
      isMock: true,
    };
  }
  return realQuery;
}
